/**
 * Staff Details - Quick Guide Components
 * Leader guides and meeting tips for individual staff
 */
import { MessageSquare, Lightbulb } from 'lucide-react';
import { VAKD_HANDLING_GUIDE, DOPE_HANDLING_GUIDE } from './constants';

// Leader's Quick Guide for Individual Staff
export const StaffQuickGuide = ({ staff, detail }) => {
  if (!detail?.personality) return null;
  
  const { vakd_type, dope_type, stress_handling, leadership_potential } = detail.personality;
  const vakdGuide = VAKD_HANDLING_GUIDE[vakd_type] || {};
  const dopeGuide = DOPE_HANDLING_GUIDE[dope_type] || {};
  
  return (
    <div className="space-y-4">
      {/* Quick Action Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-xs font-medium text-blue-600 mb-1">📞 Cara Communicate</p>
          <ul className="text-xs text-blue-800 space-y-1">
            {(vakdGuide.communication || []).slice(0, 2).map((c, i) => (
              <li key={i}>• {c}</li>
            ))}
          </ul>
        </div>
        <div className="bg-green-50 rounded-lg p-3">
          <p className="text-xs font-medium text-green-600 mb-1">📋 Cara Bagi Arahan</p>
          <ul className="text-xs text-green-800 space-y-1">
            {(vakdGuide.instruction || []).slice(0, 2).map((c, i) => (
              <li key={i}>• {c}</li>
            ))}
          </ul>
        </div>
        <div className="bg-amber-50 rounded-lg p-3">
          <p className="text-xs font-medium text-amber-600 mb-1">🎯 Cara Motivate</p>
          <ul className="text-xs text-amber-800 space-y-1">
            {(dopeGuide.motivation || []).slice(0, 2).map((c, i) => (
              <li key={i}>• {c}</li>
            ))}
          </ul>
        </div>
        <div className="bg-red-50 rounded-lg p-3">
          <p className="text-xs font-medium text-red-600 mb-1">⚠️ Jangan Buat</p>
          <ul className="text-xs text-red-800 space-y-1">
            {(dopeGuide.warning || []).slice(0, 2).map((c, i) => (
              <li key={i}>• {c}</li>
            ))}
          </ul>
        </div>
      </div>
      
      {/* Best Tasks */}
      {vakdGuide.bestTasks && (
        <div className="bg-purple-50 rounded-lg p-3">
          <p className="text-xs font-medium text-purple-600 mb-2">✨ Task Terbaik untuk Staff Ini</p>
          <div className="flex flex-wrap gap-1">
            {vakdGuide.bestTasks.map((task, i) => (
              <span key={i} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">{task}</span>
            ))}
          </div>
        </div>
      )}
      
      {/* Welfare Alert */}
      {stress_handling === 'Needs Support' && (
        <div className="bg-red-100 border border-red-300 rounded-lg p-3">
          <p className="text-sm font-medium text-red-700">🚨 Welfare Alert</p>
          <p className="text-xs text-red-600">Staff ini mungkin perlukan perhatian lebih - stress handling rendah</p>
        </div>
      )}
      
      {/* Development Path for High Potential */}
      {leadership_potential === 'High' && dopeGuide.development && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-3 border border-amber-200">
          <p className="text-xs font-medium text-amber-700 mb-1">👑 High Potential - Development Areas</p>
          <div className="flex flex-wrap gap-1">
            {dopeGuide.development.map((d, i) => (
              <span key={i} className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">{d}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// 1-on-1 Meeting Guide Generator
export const MeetingGuideGenerator = ({ staff, detail }) => {
  if (!detail?.personality) return null;
  
  const { vakd_type, dope_type, stress_handling, strengths, areas_for_improvement } = detail.personality;
  const dopeGuide = DOPE_HANDLING_GUIDE[dope_type] || {};
  
  const talkingPoints = [];
  
  // Based on DOPE type
  if (dope_type === 'Dove') {
    talkingPoints.push('Mulakan dengan check-in personal - tanya khabar keluarga');
    talkingPoints.push('Bagi assurance tentang job stability');
    talkingPoints.push('Tanya jika ada concerns yang tak disuarakan');
  } else if (dope_type === 'Owl') {
    talkingPoints.push('Prepare data/metrics sebelum meeting');
    talkingPoints.push('Discuss specific projects dengan details');
    talkingPoints.push('Bagi masa untuk mereka prepare soalan');
  } else if (dope_type === 'Peacock') {
    talkingPoints.push('Mulakan dengan recognition/praise');
    talkingPoints.push('Discuss new opportunities atau ideas');
    talkingPoints.push('Tanya tentang social dynamics dalam team');
  } else if (dope_type === 'Eagle') {
    talkingPoints.push('Terus kepada business - skip small talk');
    talkingPoints.push('Discuss results dan next challenges');
    talkingPoints.push('Tanya apa obstacles yang perlu diremove');
  }
  
  // Welfare check
  if (stress_handling === 'Needs Support' || stress_handling === 'Moderate') {
    talkingPoints.push('🚨 Check workload & stress level');
  }
  
  // Development discussion
  if (areas_for_improvement?.length > 0) {
    talkingPoints.push(`Discuss development: ${areas_for_improvement.slice(0, 2).join(', ')}`);
  }
  
  return (
    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-4 border border-indigo-200">
      <h4 className="font-bold text-indigo-800 mb-3 flex items-center gap-2">
        <MessageSquare className="w-4 h-4" />
        1-on-1 Meeting Guide
      </h4>
      <ol className="space-y-2">
        {talkingPoints.map((point, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-indigo-700">
            <span className="w-5 h-5 rounded-full bg-indigo-200 text-indigo-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
            {point}
          </li>
        ))}
      </ol>
    </div>
  );
};
